<script setup lang="ts">
// #737: 体験ファーストのヒーロー（A×B ハイブリッド、/ 用、旧 HeroSection.vue の後継）。
// - B（感情フック）: 45–55歳ベーマガ世代の「今さら」に刺す見出し。
// - A（体験）: 穴埋め写経→run のインライン Pyxel デモ。登録不要。
//
// Pyxel/Pyodide は重いため load-on-intent（「動かしてみる」を押すまで PyxelPlayer を
// マウントしない）。接続は / 側の useHead で preconnect して温める。
const { trackCta } = useCtaEvent()
const runtimeConfig = useRuntimeConfig()

// 顔はめパネル（入力は「書いている感触」を出すための飾り。run は固定コードで必ず動く）
const blankValues = ref<Record<string, string>>({
  blank1: '',
  blank2: '',
  blank3: '',
})

const showPyxel = ref(false)
const casting = ref(false)
const revealed = ref(false)
// #737: ハイドレーション完了まではボタンに click ハンドラが付かず「押しても無反応」に
// 見える（遅い回線で顕著）。その間はボタンを「準備中…」で明示し、押せるようになったら
// 「動かしてみる」に切り替える。
const ready = ref(false)

// #737: 実行コードは穴埋め入力（幅・高さ・繰り返し回数）を反映して run 時に組み立てる。
// 入力が無視される「ハリボテ」だと、わざと変な値を入れて確かめる元マイコン少年層に露見して
// ノスタルジー訴求全体の信頼を損なうため（Fable レビュー指摘）。極端値・空欄・不正入力は
// clamp / 既定値でガードし、「必ず動く魔法」は保つ。
const clampInt = (raw: string | undefined, fallback: number, min: number, max: number): number => {
  const n = Number.parseInt(raw ?? '', 10)
  if (Number.isNaN(n)) return fallback
  return Math.min(max, Math.max(min, n))
}

// 実行される Pyxel コード。onRunClick で buildPyxelCode() の結果を流し込む。
const pyxelCode = ref('')

const buildPyxelCode = (): string => {
  const w = clampInt(blankValues.value.blank1, 64, 24, 200)
  const h = clampInt(blankValues.value.blank2, 64, 24, 200)
  const n = clampInt(blankValues.value.blank3, 20, 1, 48)
  const cx = Math.round(w / 2)
  const cy = Math.round(h / 2)
  return [
    'import pyxel',
    '',
    'class App:',
    '    def __init__(self):',
    `        pyxel.init(${w}, ${h}, title='Magic')`,
    '        self.t = 0',
    '        pyxel.run(self.update, self.draw)',
    '',
    '    def update(self):',
    '        self.t += 1',
    '',
    '    def draw(self):',
    '        pyxel.cls(0)',
    `        for i in range(${n}):`,
    `            x = ${cx} + pyxel.sin(self.t * 3 + i * 18) * (4 + i)`,
    `            y = ${cy} + pyxel.cos(self.t * 2 + i * 18) * (4 + i)`,
    '            c = (i + self.t // 4) % 15 + 1',
    '            pyxel.circ(x, y, 2, c)',
    `        pyxel.text(${Math.max(2, cx - 12)}, ${Math.max(2, cy - 3)}, 'MAGIC!', pyxel.frame_count % 16)`,
    '',
    'App()',
  ].join('\n')
}

// #737: 詠唱（casting）は「最低 MIN_CAST_MS は演出を見せる」floor と、
// 「Pyxel が実際に起動したら即終える」readiness（iframe からの postMessage）を両立する。
// 遅いモバイルでも固定 2.8s で切って空フレームを見せず、実際に動いた瞬間に reveal する。
// 起動シグナルが来なければ MAX_CAST_MS で強制的に reveal する（安全弁）。
const MIN_CAST_MS = 1500
const MAX_CAST_MS = 20000

let castTimer: number | null = null
let onPyxelReady: ((e: MessageEvent) => void) | null = null

const clearCastWatch = () => {
  if (castTimer !== null) {
    window.clearTimeout(castTimer)
    castTimer = null
  }
  if (onPyxelReady) {
    window.removeEventListener('message', onPyxelReady)
    onPyxelReady = null
  }
}

const finishCasting = () => {
  clearCastWatch()
  casting.value = false
  revealed.value = true
}

const onRunClick = () => {
  if (showPyxel.value) return
  trackCta('hero_run_demo')
  pyxelCode.value = buildPyxelCode()
  showPyxel.value = true
  casting.value = true

  const startedAt = performance.now()
  // Pyxel の起動シグナルで詠唱を終える（最低 MIN_CAST_MS は演出を見せる）。
  onPyxelReady = (e: MessageEvent) => {
    if (!e.data || e.data.type !== '__pyxelAutoStarted') return
    // #749: 受信元 origin を PyxelPlayer が張るフレームに限定し、第三者フレームからの
    // 偽 __pyxelAutoStarted で詠唱を早切りされないようにする。cross-origin フレーム時は
    // sandboxOrigin、blob フォールバック時は opaque origin（"null"）。
    const sandbox = runtimeConfig.public.pyxelSandboxOrigin || ''
    const originOk = e.origin === 'null'
      || e.origin === window.location.origin
      || (!!sandbox && e.origin === sandbox)
    if (!originOk) return
    if (castTimer !== null) window.clearTimeout(castTimer)
    const remaining = Math.max(0, MIN_CAST_MS - (performance.now() - startedAt))
    castTimer = window.setTimeout(finishCasting, remaining)
  }
  window.addEventListener('message', onPyxelReady)
  // 起動シグナルが来ない場合の安全弁。
  castTimer = window.setTimeout(finishCasting, MAX_CAST_MS)
}

// #737: 値を変えてもう一度試せるように、入力パネルへ戻す（入力値は保持）。
// #2 で穴埋めが実行に効くようにしたので、「変えて→動かす」の試行錯誤を促す。
const onRetry = () => {
  trackCta('hero_run_retry')
  clearCastWatch()
  revealed.value = false
  casting.value = false
  showPyxel.value = false
}

// #737: マウント（＝ハイドレーション完了）でボタンを有効化する。
onMounted(() => {
  ready.value = true
})

// #738: モバイル `dvh` はスクロールで URL バーの表示/非表示に追従して動くため、
// `min-height: 100dvh` のままだと下スクロールのたびに .hero2 が伸縮し、
// 中の背景 <img>（親を 100% で埋める）がリサイズされて CLS になる。
// V1 の解と同じく、mount 時点の innerHeight を `--initial-vh` に固定して
// height をロックする。SSR/初回描画は `100svh`（最小固定＝暗色オーバーレイが
// 途切れないほうに倒す）で fallback する。
let vhOrientationHandler: (() => void) | null = null
let vhResizeHandler: (() => void) | null = null

const updateInitialVh = () => {
  document.documentElement.style.setProperty('--initial-vh', `${window.innerHeight}px`)
}

onMounted(() => {
  updateInitialVh()
  vhOrientationHandler = updateInitialVh
  window.addEventListener('orientationchange', vhOrientationHandler)

  // デスクトップは URL バーによる高さ変動が無いので、通常の resize で追従。
  // モバイルは意図的に purchase しない（URL バー動作で resize が発火し CLS が戻る）。
  if (window.innerWidth > 600) {
    let ticking = false
    vhResizeHandler = () => {
      if (ticking) return
      ticking = true
      requestAnimationFrame(() => {
        updateInitialVh()
        ticking = false
      })
    }
    window.addEventListener('resize', vhResizeHandler)
  }
})

onUnmounted(() => {
  clearCastWatch()
  if (vhOrientationHandler) window.removeEventListener('orientationchange', vhOrientationHandler)
  if (vhResizeHandler) window.removeEventListener('resize', vhResizeHandler)
})
</script>

<template>
  <div class="hero2">
    <img
      src="@/assets/images/beginning/TheBeginning_Cut1.webp"
      alt=""
      class="hero2-bg"
      fetchpriority="high"
      decoding="async"
    >
    <div class="hero2-overlay" />

    <div class="hero2-inner">
      <!-- B: 感情フック -->
      <p class="hero2-eyebrow">
        「今さら」だと思っている、あなたへ
      </p>
      <h1 class="hero2-headline">
        動くまで、打ち込んだ、<br>あの夜の、続きを。
      </h1>

      <!-- A: 触って動く体験（登録不要） -->
      <div class="demo">
        <div
          v-if="!showPyxel"
          class="demo-panel"
        >
          <p class="demo-hint">
            <span class="demo-hint-arrow">▶</span> まずは触ってみて。空欄を埋めて「動かす」だけ。<span class="demo-noreg">登録不要</span>
          </p>
          <pre class="demo-code"><span class="kw">import</span> pyxel

pyxel.init(<input
  v-model="blankValues.blank1"
  class="blank"
  type="text"
  inputmode="numeric"
  placeholder="64"
  maxlength="3"
  aria-label="幅"
>, <input
  v-model="blankValues.blank2"
  class="blank"
  type="text"
  inputmode="numeric"
  placeholder="64"
  maxlength="3"
  aria-label="高さ"
>)

<span class="kw">for</span> i <span class="kw">in</span> range(<input
  v-model="blankValues.blank3"
  class="blank"
  type="text"
  inputmode="numeric"
  placeholder="20"
  maxlength="3"
  aria-label="繰り返し回数"
>):
    x = pyxel.sin(i * 18) * 20
    y = pyxel.cos(i * 18) * 20
    pyxel.circ(32 + x, 32 + y, 2, 7)

pyxel.show()</pre>
          <button
            class="run-button"
            type="button"
            :disabled="!ready"
            @click="onRunClick"
          >
            <template v-if="ready">
              <span class="run-icon">▶</span> 動かしてみる
            </template>
            <template v-else>
              <span
                class="run-spinner"
                aria-hidden="true"
              /> 準備中…
            </template>
          </button>
        </div>

        <!-- 実行結果 -->
        <div
          v-else
          class="demo-stage"
        >
          <div class="pyxel-frame">
            <ClientOnly>
              <PyxelPlayer
                :code="pyxelCode"
                auto-start
              />
            </ClientOnly>
            <div
              v-if="casting"
              class="casting"
              role="status"
              aria-live="polite"
            >
              <div class="casting-orb" />
              <p class="casting-text">
                ✨ 魔法を詠唱中<span class="dots"><span>.</span><span>.</span><span>.</span></span>
              </p>
              <p class="casting-note">
                初回は少しだけ時間がかかります
              </p>
            </div>
          </div>
          <p
            class="magic-line"
            :class="{ show: revealed }"
          >
            ── これが、魔法。
          </p>
          <button
            v-if="revealed"
            class="retry-button"
            type="button"
            @click="onRetry"
          >
            <span class="retry-icon">↻</span> 値を変えて、もう一度
          </button>
        </div>
      </div>

      <!-- CTA -->
      <div class="hero2-cta">
        <button
          class="cta-primary"
          type="button"
          @click="trackCta('冒険を始める（hero2）'); navigateTo('/signup')"
        >
          冒険を始める<span class="cta-free">無料</span>
        </button>
        <NuxtLink
          to="/signin"
          class="cta-signin"
        >
          登録済みの方はサインイン
        </NuxtLink>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* ===== 土台（モバイルファースト） ===== */
.hero2 {
  position: relative;
  width: 100%;
  /* #738: モバイル CLS 対策。SSR/初回描画は `100svh`（最小固定）でオーバーレイが
     途切れないほうに倒し、mount 後は JS で `--initial-vh` に差し替えて完全固定。
     min-height ではなく height を使う（内側コンテンツで伸びると再度 CLS になる）。
     デスクトップは #740 で min-height に戻す（下部 @media 参照）。 */
  height: var(--initial-vh, 100svh);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  font-family: "Noto Sans JP", sans-serif;
  color: #efe7d6;
}

.hero2-bg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 0;
}

.hero2-overlay {
  position: absolute;
  inset: 0;
  z-index: 1;
  background:
    radial-gradient(120% 90% at 50% 20%, rgba(0, 0, 0, 0.25), rgba(0, 0, 0, 0.72) 78%),
    linear-gradient(180deg, rgba(10, 8, 5, 0.35) 0%, rgba(10, 8, 5, 0.78) 100%);
}

.hero2-inner {
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: 640px;
  margin: 0 auto;
  padding: 28px 14px 40px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* ===== B: 感情フック ===== */
.hero2-eyebrow {
  font-family: 'Noto-Serif-JP', serif;
  font-size: clamp(13px, 3.8vw, 22px);
  letter-spacing: 0.04em;
  color: #e6c98a;
  margin: 0 0 12px;
  text-shadow: 0 1px 10px rgba(0, 0, 0, 0.9);
}

.hero2-headline {
  font-family: 'Noto-Serif-JP', serif;
  /* 1行目「動くまで、打ち込んだ、」は句読点込み11文字。小さめの画面でも
     1行に収まるよう vw を抑える（#737 ユーザーフィードバック）。 */
  font-size: clamp(26px, 8.2vw, 52px);
  font-weight: 700;
  line-height: 1.32;
  margin: 0 0 26px;
  text-shadow: 0 2px 22px rgba(0, 0, 0, 0.85);
}

/* ===== A: 体験デモ ===== */
.demo {
  width: 100%;
  margin-bottom: 28px;
}

.demo-panel {
  background: rgba(18, 14, 9, 0.82);
  border: 1px solid rgba(190, 165, 120, 0.32);
  border-radius: 12px;
  padding: 16px 14px 18px;
  text-align: left;
  box-shadow: 0 12px 40px -14px rgba(0, 0, 0, 0.8), 0 0 40px rgba(190, 160, 100, 0.08);
  backdrop-filter: blur(3px);
}

.demo-hint {
  font-size: clamp(12px, 3.4vw, 14px);
  color: #d9cbaa;
  margin: 0 0 12px;
  line-height: 1.6;
  text-align: center;
}

.demo-hint-arrow { color: #7dffb0; }

.demo-noreg {
  display: inline-block;
  font-size: 0.82em;
  font-weight: 700;
  color: #0d1c12;
  background: #7dffb0;
  border-radius: 999px;
  padding: 1px 9px;
  margin-left: 4px;
  vertical-align: middle;
}

.demo-code {
  font-family: 'Google Sans Code', 'Courier New', monospace;
  font-size: clamp(11px, 3.1vw, 13.5px);
  line-height: 1.85;
  color: #c8b888;
  white-space: pre-wrap;
  word-break: break-word;
  margin: 0;
}

.kw { color: #88aacc; }

.blank {
  width: 3.2em;
  padding: 1px 5px;
  text-align: center;
  color: #ffe08a;
  font-family: inherit;
  font-size: inherit;
  background: rgba(255, 224, 138, 0.12);
  border: 1px dashed rgba(255, 224, 138, 0.55);
  border-radius: 4px;
  outline: none;
}

.blank:focus {
  border-color: #ffe08a;
  background: rgba(255, 224, 138, 0.22);
  box-shadow: 0 0 10px rgba(255, 224, 138, 0.25);
}

.blank::placeholder { color: rgba(255, 224, 138, 0.32); }

.run-button {
  display: block;
  width: 100%;
  margin: 16px 0 0;
  padding: 14px 24px;
  font-family: 'Google Sans Code', 'Courier New', monospace;
  font-size: clamp(15px, 4.4vw, 18px);
  font-weight: 700;
  color: #0d1c12;
  background: linear-gradient(135deg, #7dffb0, #38d98a);
  border: none;
  border-radius: 10px;
  cursor: pointer;
  box-shadow: 0 6px 20px -6px rgba(56, 217, 138, 0.6);
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.run-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 26px -6px rgba(56, 217, 138, 0.7);
}

.run-button:active { transform: translateY(0); }

.run-button:disabled {
  cursor: progress;
  opacity: 0.88;
}

.run-button:disabled:hover {
  transform: none;
  box-shadow: 0 6px 20px -6px rgba(56, 217, 138, 0.6);
}

.run-icon { font-size: 0.85em; }

/* #737: ハイドレーション前の「準備中…」スピナー */
.run-spinner {
  display: inline-block;
  width: 1em;
  height: 1em;
  border: 2px solid rgba(13, 28, 18, 0.3);
  border-top-color: #0d1c12;
  border-radius: 50%;
  vertical-align: -0.15em;
  animation: run-spin 0.8s linear infinite;
}

@keyframes run-spin {
  to { transform: rotate(360deg); }
}

/* ===== 実行ステージ ===== */
.demo-stage {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.pyxel-frame {
  position: relative;
  width: 100%;
  max-width: 340px;
  aspect-ratio: 1;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid rgba(190, 165, 120, 0.3);
  background: #050505;
  box-shadow: 0 12px 40px -14px rgba(0, 0, 0, 0.85);
}

.pyxel-frame :deep(.pixel-player) { height: 100%; }

.pyxel-frame :deep(.pixel-player-inner) {
  width: 100%;
  height: 100%;
  max-width: none;
  max-height: none;
  border: 0;
}

.casting {
  position: absolute;
  inset: 0;
  z-index: 3;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: radial-gradient(circle at 50% 45%, #1a1206, #0a0704 80%);
  padding: 16px;
  text-align: center;
}

.casting-orb {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: conic-gradient(from 0deg, #ff004d, #ffa300, #ffec27, #00e436, #29adff, #ff77a8, #ff004d);
  box-shadow: 0 0 22px 3px rgba(255, 200, 90, 0.4);
  animation: orb-spin 1.6s linear infinite;
}

.casting-text {
  font-family: 'Google Sans Code', 'Courier New', monospace;
  font-size: 14px;
  color: #ffe08a;
  margin: 0;
}

.casting-note {
  font-size: 11px;
  color: #b3a07a;
  margin: 0;
}

.dots span { animation: blink 1.4s infinite both; }
.dots span:nth-child(2) { animation-delay: 0.2s; }
.dots span:nth-child(3) { animation-delay: 0.4s; }

.magic-line {
  font-family: 'Noto-Serif-JP', serif;
  font-size: clamp(15px, 4.4vw, 19px);
  font-weight: 700;
  color: #ffd780;
  margin: 16px 0 0;
  opacity: 0;
  transform: translateY(6px);
  transition: opacity 0.7s ease, transform 0.7s ease;
  text-shadow: 0 0 24px rgba(255, 200, 90, 0.5);
}

.magic-line.show {
  opacity: 1;
  transform: translateY(0);
}

/* もう一度ためすボタン（値を変えて再挑戦） */
.retry-button {
  margin: 16px 0 0;
  padding: 9px 22px;
  font-family: 'Google Sans Code', 'Courier New', monospace;
  font-size: clamp(12px, 3.4vw, 14px);
  font-weight: 700;
  color: #e6c98a;
  background: rgba(255, 224, 138, 0.08);
  border: 1px solid rgba(230, 201, 138, 0.5);
  border-radius: 999px;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease, transform 0.15s ease;
}

.retry-button:hover {
  background: rgba(255, 224, 138, 0.16);
  border-color: #e6c98a;
  transform: translateY(-1px);
}

.retry-button:active { transform: translateY(0); }

.retry-icon { font-size: 0.95em; }

/* ===== CTA ===== */
.hero2-cta {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  width: 100%;
}

.cta-primary {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 15px 40px;
  font-size: clamp(16px, 4.6vw, 19px);
  font-weight: 700;
  color: #1a1510;
  background: linear-gradient(135deg, #d9a83e, #f0c96a);
  border: none;
  border-radius: 999px;
  cursor: pointer;
  box-shadow: 0 8px 26px -8px rgba(216, 160, 60, 0.7);
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.cta-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 32px -8px rgba(216, 160, 60, 0.8);
}

.cta-free {
  font-size: 0.72em;
  font-weight: 800;
  color: #fff;
  background: #1a7566;
  border-radius: 999px;
  padding: 2px 10px;
}

.cta-signin {
  font-size: 13px;
  color: #e9e0cf;
  text-decoration: underline;
  text-underline-offset: 3px;
  text-shadow: 0 1px 8px rgba(0, 0, 0, 0.9);
}

.cta-signin:hover { color: #fff; }

/* ===== 演出 ===== */
@keyframes orb-spin { to { transform: rotate(360deg); } }
@keyframes blink { 0%, 100% { opacity: 0.2; } 50% { opacity: 1; } }

@media (prefers-reduced-motion: reduce) {
  .casting-orb { animation: none; }
  .dots span { animation: none; }
  .magic-line { transition: none; }
  .run-spinner { animation: none; }
}

/* ===== デスクトップ ===== */
@media (min-width: 600.02px) {
  /* #740: デスクトップは URL バー動作がないので CLS の発症条件がない。
     min-height に戻すことで、縮小ウィンドウ（縦が中途半端に短い）で
     内側コンテンツが --initial-vh を超えたときに align-items: center + overflow: hidden
     で上端が見切れる回帰を解消する。モバイルは #738 のロックを維持。 */
  .hero2 {
    height: auto;
    min-height: var(--initial-vh, 100svh);
    overflow: visible;
  }

  .hero2-inner {
    max-width: 680px;
    padding: 40px 24px 56px;
  }

  .demo-panel { padding: 22px 24px 24px; }

  .run-button { width: auto; margin: 18px auto 0; padding: 14px 56px; }

  .pyxel-frame { max-width: 380px; }
}
</style>
